import React, { useEffect, useState } from 'react';
import { api, API_BASE } from '../../api/client';

interface Feedback {
  id: number;
  fullName: string;
  email: string;
  phone: string | null;
  message: string;
  fileS3: string | null;
  originalFileName: string | null;
  status: string;
  createdAt: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  NEW:        { label: 'Новое',       color: '#e74c3c' },
  IN_PROCESS: { label: 'В работе',    color: '#f39c12' },
  DONE:       { label: 'Завершено',   color: '#27ae60' },
};

export default function AdminFeedbackPage() {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [selected, setSelected] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/feedback');
      setFeedbacks(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id: number) => {
    if (!window.confirm('Удалить обращение?')) return;
    await api.delete(`/feedback/${id}`);
    setSelected(null);
    await load();
  };

  const changeStatus = async (id: number, status: string) => {
    setUpdatingStatus(true);
    try {
      const r = await api.patch(`/feedback/${id}/status?status=${status}`);
      setSelected(r.data);
      await load();
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Обращения ({feedbacks.length})</h2>

      {loading ? (
        <p style={styles.empty}>Загрузка...</p>
      ) : feedbacks.length === 0 ? (
        <p style={styles.empty}>Обращений пока нет</p>
      ) : (
        <>
          <div style={styles.layout} className="admin-feedback-layout">

            {/* Список */}
            <div style={styles.list}>
              {feedbacks.map(f => (
                <div
                  key={f.id}
                  style={{ ...styles.item, ...(selected?.id === f.id ? styles.itemActive : {}) }}
                  onClick={() => setSelected(f)}
                >
                  <div style={styles.itemHeader}>
                    <span style={styles.itemName}>{f.fullName}</span>
                    <span style={{
                      ...styles.badge,
                      background: STATUS_LABELS[f.status]?.color || '#888',
                    }}>
                      {STATUS_LABELS[f.status]?.label || f.status}
                    </span>
                  </div>
                  <div style={styles.itemEmail}>{f.email}</div>
                  <div style={styles.itemDate}>
                    {f.createdAt ? new Date(f.createdAt).toLocaleDateString('ru-RU', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    }) : ''}
                  </div>
                  <div style={styles.itemPreview}>
                    {f.message.slice(0, 80)}{f.message.length > 80 ? '...' : ''}
                  </div>
                </div>
              ))}
            </div>

            {/* Детали */}
            <div style={styles.detail}>
              {selected ? (
                <>
                  <div style={styles.detailHeader}>
                    <h3 style={styles.detailName}>{selected.fullName}</h3>
                    <button onClick={() => remove(selected.id)} style={styles.btnDanger}>
                      🗑 Удалить
                    </button>
                  </div>

                  {/* Кнопки смены статуса */}
                  <div style={styles.statusBar}>
                    <span style={styles.statusLabel}>Статус:</span>
                    {(['NEW', 'IN_PROCESS', 'DONE'] as const).map(s => (
                      <button
                        key={s}
                        disabled={updatingStatus || selected.status === s}
                        onClick={() => changeStatus(selected.id, s)}
                        style={{
                          ...styles.statusBtn,
                          background: selected.status === s
                            ? STATUS_LABELS[s].color
                            : '#f0f4fb',
                          color: selected.status === s ? '#fff' : '#555',
                          opacity: updatingStatus ? 0.6 : 1,
                        }}
                      >
                        {STATUS_LABELS[s].label}
                      </button>
                    ))}
                  </div>

                  <div style={styles.detailFields}>
                    <Row label="Email" value={<a href={`mailto:${selected.email}`} style={styles.link}>{selected.email}</a>} />
                    {selected.phone && <Row label="Телефон" value={<a href={`tel:${selected.phone}`} style={styles.link}>{selected.phone}</a>} />}
                    <Row label="Статус" value={
                      <span style={{ color: STATUS_LABELS[selected.status]?.color || '#888', fontWeight: 600 }}>
                        {STATUS_LABELS[selected.status]?.label || selected.status}
                      </span>
                    } />
                    <Row label="Дата" value={selected.createdAt ? new Date(selected.createdAt).toLocaleString('ru-RU') : ''} />
                  </div>

                  <div style={styles.messageBox}>
                    <div style={styles.messageLabel}>Сообщение:</div>
                    <div style={styles.messageText}>{selected.message}</div>
                  </div>

                  {selected.fileS3 && (
                    <FileViewer feedback={selected} />
                  )}
                </>
              ) : (
                <div style={styles.detailEmpty}>
                  <span style={{ fontSize: '2rem' }}>📬</span>
                  <p>Выберите обращение из списка</p>
                </div>
              )}
            </div>
          </div>
          <style>{`
            @media (max-width: 768px) {
              .admin-feedback-layout {
                grid-template-columns: 1fr !important;
              }
            }
          `}</style>
        </>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: '0.9rem' }}>
      <span style={{ color: '#888', minWidth: 80 }}>{label}:</span>
      <span style={{ color: '#333' }}>{value}</span>
    </div>
  );
}

function FileViewer({ feedback }: { feedback: Feedback }) {
  const name = feedback.originalFileName || feedback.fileS3 || '';
  const ext = name.split('.').pop()?.toLowerCase() || '';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
  const isPdf = ext === 'pdf';

  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [loadingFile, setLoadingFile] = React.useState(false);
  const [fileError, setFileError] = React.useState(false);

  React.useEffect(() => {
    if (!feedback.fileS3) return;
    setLoadingFile(true);
    setFileError(false);
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/feedback/${feedback.id}/file`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.blob();
      })
      .then(blob => setBlobUrl(URL.createObjectURL(blob)))
      .catch(() => setFileError(true))
      .finally(() => setLoadingFile(false));
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback.id]);

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = name || 'file';
    a.click();
  };

  return (
    <div style={fv.box}>
      <div style={fv.header}>
        <span style={fv.icon}>{isImage ? '🖼️' : isPdf ? '📄' : '📎'}</span>
        <span style={fv.name}>{name}</span>
        <button
          onClick={handleDownload}
          disabled={!blobUrl}
          style={{ ...fv.downloadBtn, opacity: blobUrl ? 1 : 0.5 }}
        >
          {loadingFile ? 'Загрузка...' : '⬇️ Скачать'}
        </button>
      </div>

      {loadingFile && <div style={fv.loading}>Загрузка файла...</div>}

      {fileError && (
        <div style={{ padding: '12px 16px', color: '#e74c3c', fontSize: '0.88rem' }}>
          Не удалось загрузить файл. Возможно файл был удалён из MinIO.
        </div>
      )}

      {!loadingFile && blobUrl && isImage && (
        <img src={blobUrl} alt={name} style={fv.img} />
      )}

      {!loadingFile && blobUrl && isPdf && (
        <iframe src={blobUrl} title={name} style={fv.iframe} />
      )}

      {!loadingFile && blobUrl && !isImage && !isPdf && (
        <div style={fv.otherFile}>
          📎 Файл загружен. Нажмите «Скачать» чтобы открыть.
        </div>
      )}
    </div>
  );
}

const fv: Record<string, React.CSSProperties> = {
  box: { marginTop: 16, border: '1px solid #e0e6f0', borderRadius: 8, overflow: 'hidden' },
  header: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 14px', background: '#f8f9fa',
    borderBottom: '1px solid #e0e6f0',
  },
  icon: { fontSize: '1.2rem' },
  name: { flex: 1, fontSize: '0.88rem', color: '#444', wordBreak: 'break-all' as const },
  downloadBtn: {
    padding: '5px 12px', background: '#1a3a6b',
    color: '#fff', border: 'none', borderRadius: 6,
    fontSize: '0.82rem', cursor: 'pointer', whiteSpace: 'nowrap' as const,
  },
  img: { width: '100%', maxHeight: 400, objectFit: 'contain' as const, display: 'block', background: '#f0f0f0' },
  iframe: { width: '100%', height: 500, border: 'none', display: 'block' },
  loading: { padding: '20px', textAlign: 'center' as const, color: '#888', fontSize: '0.9rem' },
  otherFile: { padding: '16px', color: '#555', fontSize: '0.9rem' },
};

const styles: Record<string, React.CSSProperties> = {
  page: { padding: 'clamp(16px, 4vw, 32px)', maxWidth: 1100, margin: '0 auto' },
  title: { color: '#1a3a6b', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', marginBottom: 'clamp(16px, 3vw, 24px)' },
  empty: { color: '#888', padding: 40, textAlign: 'center' },

  layout: { display: 'grid', gridTemplateColumns: 'minmax(300px, 340px) 1fr', gap: 20, alignItems: 'start' },

  list: {
    background: '#fff', borderRadius: 10,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    overflow: 'hidden', maxHeight: '75vh', overflowY: 'auto',
  },
  item: {
    padding: '14px 16px', borderBottom: '1px solid #f0f4fb',
    cursor: 'pointer', transition: 'background 0.15s',
  },
  itemActive: { background: '#f0f4fb' },
  itemHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 6 },
  itemName: { fontWeight: 600, color: '#1a2a4a', fontSize: '0.95rem', wordBreak: 'break-word' },
  badge: {
    fontSize: '0.72rem', color: '#fff', fontWeight: 600,
    padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
  },
  itemEmail: { fontSize: '0.82rem', color: '#888', marginBottom: 4, wordBreak: 'break-word' },
  itemDate: { fontSize: '0.78rem', color: '#aaa', marginBottom: 6 },
  itemPreview: { fontSize: '0.82rem', color: '#666', lineHeight: 1.4, wordBreak: 'break-word' },

  detail: {
    background: '#fff', borderRadius: 10,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    padding: 'clamp(16px, 3vw, 24px)', minHeight: 300,
  },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 },
  detailName: { color: '#1a3a6b', fontSize: 'clamp(1rem, 2vw, 1.2rem)', wordBreak: 'break-word' },
  detailFields: { marginBottom: 20 },
  messageBox: {
    background: '#f8f9fa', borderRadius: 8,
    padding: '16px', marginBottom: 16,
  },
  messageLabel: { fontWeight: 600, color: '#555', fontSize: '0.85rem', marginBottom: 8 },
  messageText: { color: '#333', fontSize: '0.95rem', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  fileLink: {
    display: 'inline-block', color: '#1a3a6b',
    fontSize: '0.9rem', fontWeight: 600,
  },
  detailEmpty: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    height: 200, color: '#aaa', gap: 12,
  },
  link: { color: '#1a3a6b', textDecoration: 'none', wordBreak: 'break-word' },
  btnDanger: {
    padding: '6px 14px', background: '#e74c3c',
    color: '#fff', border: 'none', borderRadius: 6,
    fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  statusBar: {
    display: 'flex', alignItems: 'center', gap: 8,
    marginBottom: 20, flexWrap: 'wrap' as const,
  },
  statusLabel: { fontSize: '0.85rem', color: '#888', marginRight: 4 },
  statusBtn: {
    padding: '5px 14px', border: 'none', borderRadius: 16,
    fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.15s', whiteSpace: 'nowrap',
  },
};
